// Project status types
export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';

// Budget interface
export interface ProjectBudget {
  estimated: number;
  actual: number;
  currency: string;
}

// Client reference (populated)
export interface ProjectClient {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Creator reference (populated)
export interface ProjectCreator {
  _id: string;
  profile: {
    firstName: string;
    lastName: string;
  };
}

// Main Project interface
export interface Project {
  _id: string;
  organizationId: string;
  createdById: string | ProjectCreator;

  // Core fields
  name: string;
  description?: string;
  status: ProjectStatus;

  // Dates
  startDate?: string;
  dueDate?: string;
  completedAt?: string;

  // Relationships
  clientId?: string | ProjectClient;

  // Budget
  budget?: ProjectBudget;

  // Metadata
  color?: string;
  tags: string[];

  // Computed (from API)
  taskCount?: number;
  completedTaskCount?: number;
  todoTaskCount?: number;
  inProgressTaskCount?: number;
  noteCount?: number;
  progress?: number;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// Project with populated fields
export interface ProjectWithRelations extends Omit<Project, 'clientId' | 'createdById'> {
  clientId?: ProjectClient;
  createdById?: ProjectCreator;
}

// Create project request
export interface CreateProjectRequest {
  name: string;
  description?: string;
  status?: ProjectStatus;
  startDate?: string;
  dueDate?: string;
  clientId?: string;
  budget?: ProjectBudget;
  color?: string;
  tags?: string[];
}

// Update project request
export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  status?: ProjectStatus;
  startDate?: string | null;
  dueDate?: string | null;
  clientId?: string | null;
  budget?: ProjectBudget | null;
  color?: string | null;
  tags?: string[];
}

// Project filters
export interface ProjectFilters {
  status?: ProjectStatus[];
  clientId?: string;
  tags?: string[];
  includeArchived?: boolean;
  dueBefore?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'dueDate' | 'status';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Pagination
export interface ProjectPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Projects list response
export interface ProjectsResponse {
  success: boolean;
  data: {
    projects: ProjectWithRelations[];
    pagination: ProjectPagination;
  };
}

// Single project response
export interface ProjectResponse {
  success: boolean;
  data: {
    project: ProjectWithRelations;
  };
}

// Status update response
export interface ProjectStatusResponse {
  success: boolean;
  data: {
    project: ProjectWithRelations;
    previousStatus: ProjectStatus;
    message: string;
  };
}

// Project tasks response
export interface ProjectTasksResponse {
  success: boolean;
  data: {
    tasks: any[]; // Uses existing Task type
  };
}

// Project notes response
export interface ProjectNotesResponse {
  success: boolean;
  data: {
    notes: any[]; // Uses existing Note type
  };
}

// Status options for UI
export const PROJECT_STATUS_OPTIONS: {
  value: ProjectStatus;
  label: string;
  color: string;
  bgColor: string;
}[] = [
  { value: 'planning', label: 'Planning', color: '#1976d2', bgColor: '#e3f2fd' },
  { value: 'active', label: 'Active', color: '#2e7d32', bgColor: '#e8f5e9' },
  { value: 'on_hold', label: 'On Hold', color: '#ed6c02', bgColor: '#fff3e0' },
  { value: 'completed', label: 'Completed', color: '#757575', bgColor: '#f5f5f5' },
  { value: 'cancelled', label: 'Cancelled', color: '#d32f2f', bgColor: '#ffebee' },
];

// Project color options
export const PROJECT_COLORS = [
  '#1976d2', // Blue
  '#2e7d32', // Green
  '#ed6c02', // Orange
  '#9c27b0', // Purple
  '#d32f2f', // Red
  '#00838f', // Cyan
  '#5d4037', // Brown
  '#546e7a', // Blue Gray
];
